import React from 'react'
import { FormattedMessage } from 'react-intl'
import { View } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import {
  EAP_TO_HPI_LINK_DESTINATION,
  getBreakpointValue,
  useGetEapToHpiEligibilityText,
} from '@lyrahealth-inc/shared-app-logic'
import { AttentionIcon, BodyText, BodyTextSize, InnovationIcon, Link, tID } from '@lyrahealth-inc/ui-core-crossplatform'

import { XButton } from '../../atoms/xButton/XButton'
import { IS_WEB } from '../../constants'

const BannerContainer = styled.View<{ backgroundColor: string; applyContainerStyling?: boolean }>(
  ({ backgroundColor, applyContainerStyling, theme }) => ({
    backgroundColor,
    zIndex: 99,
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    ...(applyContainerStyling && {
      paddingVertical: getBreakpointValue(theme.breakpoints, {
        isMinWidthDesktop: '30px',
        isMinWidthTablet: '16px',
        default: '12px',
      }),
      paddingHorizontal: getBreakpointValue(theme.breakpoints, {
        isMinWidthDesktop: '32px',
        isMinWidthTablet: '16px',
        default: '12px',
      }),
    }),
  }),
)

const BannerContent = styled.View<{ color: string }>(({ theme, color }) => ({
  color,
  display: 'flex',
  flexDirection: 'row',
  alignItems: theme.breakpoints.isMobileSized ? 'flex-start' : 'center',
  padding: theme.breakpoints.isMobileSized
    ? 0
    : `0 ${IS_WEB ? theme.spacing['56px'] : theme.spacing['24px']} 0 ${theme.spacing['24px']}`,
}))

const BannerIconContainer = styled(View)(({ theme }) => ({
  ...(theme.breakpoints.isMobileSized && { paddingTop: theme.spacing['8px'] }),
  paddingRight: theme.spacing['8px'],
}))

const BannerText = styled(BodyText)(({ color, theme }) => ({
  color,
  textAlign: 'left',
  ...(theme.breakpoints.isMobileSized && { padding: `${theme.spacing['8px']} 0` }),
}))

const StyledAttentionIcon = styled(AttentionIcon)(({}) => ({
  padding: 2,
}))

const StyledInnovationIcon = styled(InnovationIcon)(({}) => ({
  padding: 2,
}))

export interface EapToHpiBannerProps {
  onPressLink: (linkDestination: EAP_TO_HPI_LINK_DESTINATION) => void
  onClose?: () => void
  sessionsRemaining: number
  dependentsName?: string
  hasHpi: boolean
  hasPaymentInfo: boolean
  applyContainerStyling?: boolean
}

/**
 * Top banner within the app to warn user about their ineligible EAP eligibility status.
 */
export const EapToHpiBanner: React.FC<EapToHpiBannerProps> = ({
  onPressLink,
  onClose,
  sessionsRemaining,
  dependentsName,
  hasHpi,
  hasPaymentInfo,
  applyContainerStyling = false,
}) => {
  const theme = useTheme()
  const { colors } = theme
  const { eapToHpiInfoBanner, eapToHpiWarningBanner } = colors.components
  const isMobileSized = theme?.breakpoints?.isMobileSized

  const { descriptionText, linkText, linkDestination } = useGetEapToHpiEligibilityText({
    hasHpi,
    hasPaymentInfo,
    sessionsRemaining,
    dependentsName,
  })

  const colorPalette = sessionsRemaining === 1 ? eapToHpiInfoBanner : eapToHpiWarningBanner

  return (
    <BannerContainer
      applyContainerStyling={applyContainerStyling}
      backgroundColor={colorPalette.background}
      testID={tID(
        `EapToHpiBanner-${sessionsRemaining}_session-${dependentsName ? 'dep' : 'self'}-${
          hasHpi ? 'hpi' : hasPaymentInfo ? 'pay' : 'cost'
        }`,
      )}
    >
      <BannerContent color={colorPalette.text}>
        <BannerIconContainer>
          {sessionsRemaining === 1 ? (
            <StyledInnovationIcon size={24} color={colorPalette.text} />
          ) : (
            <StyledAttentionIcon size={24} fillColor={colorPalette.text} />
          )}
        </BannerIconContainer>
        <BannerText
          color={colorPalette.text}
          size={!isMobileSized && IS_WEB ? BodyTextSize.DEFAULT : BodyTextSize.SMALL}
        >
          <FormattedMessage
            {...descriptionText}
            values={{
              dependentsName,
              hasDualEntitlementEnabled: false,
              isTherapy: false,
              textStyle: (text: string) => <BodyText color={colorPalette.text}>{text}</BodyText>,
            }}
          />
          &nbsp;
          <FormattedMessage
            {...linkText}
            values={{
              textStyle: (text: string) => <BodyText color={colorPalette.text}>{text}</BodyText>,
              link: (text: string) => (
                <Link
                  testID={tID('EapToHpiBanner-link')}
                  style={{
                    color: colorPalette.text,
                    fontWeight: '500',
                    textDecorationLine: 'underline',
                  }}
                  size={!isMobileSized && IS_WEB ? BodyTextSize.DEFAULT : BodyTextSize.SMALL}
                  text={text}
                  data-testid='confirm-coverage-link'
                  onPress={() => onPressLink(linkDestination)}
                >
                  {text}
                </Link>
              ),
            }}
          />
        </BannerText>
        {isMobileSized && onClose ? (
          <View style={{ marginLeft: 12, justifyContent: 'center', alignSelf: 'baseline' }}>
            <XButton
              showExitText={false}
              onPress={onClose}
              testID={tID('EapToHpiBanner-dismiss-mobile')}
              accessibilityLabel='Dismiss eligibility notice'
              size={24}
            />
          </View>
        ) : null}
      </BannerContent>

      {!isMobileSized && IS_WEB && onClose ? (
        <View style={{ position: 'absolute', right: 16 }}>
          <XButton
            showExitText={false}
            onPress={onClose}
            testID={tID('EapToHpiBanner-dismiss')}
            accessibilityLabel='Dismiss eligibility notice'
          />
        </View>
      ) : null}
    </BannerContainer>
  )
}
